import type { GpsSample, HitEvent } from './types';

/**
 * Detect vertical-acceleration spikes that pass the speed gate, exceed a
 * k·σ threshold against the rolling baseline, and respect a refractory
 * window so a single bumpy bit doesn't fire 5 events.
 *
 * Inputs are road-frame vertical acceleration (m/s², gravity removed) from
 * GravityEstimator.feed(...).aVert — NOT raw device-frame values.
 *
 * Tunables (defaults match SPEC § Hit detection):
 *   speedGateKmh = 15
 *   windowSamples = 250  (5 s at 50 Hz)
 *   k = 4
 *   refractoryMs = 300
 */
export interface HitDetectorOpts {
  speedGateKmh?: number;
  windowSamples?: number;
  k?: number;
  refractoryMs?: number;
  /** Floor on σ to avoid threshold-collapse on perfectly smooth tarmac. m/s² */
  sigmaFloor?: number;
  /** g_threshold for confidence normalisation. m/s². */
  thresholdG?: number;
  deviceClass: string;
  sampleRateHz: number;
}

export class HitDetector {
  private readonly speedGateKmh: number;
  private readonly windowSamples: number;
  private readonly k: number;
  private readonly refractoryMs: number;
  private readonly sigmaFloor: number;
  private readonly thresholdG: number;
  private readonly deviceClass: string;
  private readonly sampleRateHz: number;

  private buf: number[] = [];
  private sum = 0;
  private sumSq = 0;
  private lastFireTs = 0;
  private lastGps: GpsSample | null = null;
  /**
   * Signed vertical-accel value at the moment we saw the largest
   * deviation in the current candidate. Sign distinguishes
   * pothole (negative — wheel dropped) from speed bump
   * (positive — wheel lifted). The magnitude is what feeds confidence.
   */
  private peakSignedSinceCandidate = 0;
  private candidateStartTs = 0;

  constructor(opts: HitDetectorOpts) {
    this.speedGateKmh = opts.speedGateKmh ?? 15;
    this.windowSamples = opts.windowSamples ?? 250;
    this.k = opts.k ?? 4;
    this.refractoryMs = opts.refractoryMs ?? 300;
    this.sigmaFloor = opts.sigmaFloor ?? 0.3;
    this.thresholdG = opts.thresholdG ?? 5; // ≈ 0.5g
    this.deviceClass = opts.deviceClass;
    this.sampleRateHz = opts.sampleRateHz;
  }

  setGps(s: GpsSample) {
    this.lastGps = s;
  }

  /** Returns a HitEvent if this sample completes a fire-worthy candidate. */
  feed(aVert: number, ts: number): HitEvent | null {
    const gps = this.lastGps;
    // Update rolling stats regardless of speed gate — keep baseline current
    this.pushSample(aVert);

    if (!gps || gps.speedKmh < this.speedGateKmh) return null;
    if (ts - this.lastFireTs < this.refractoryMs) return null;
    if (this.buf.length < Math.min(50, this.windowSamples / 2)) return null;

    const mean = this.sum / this.buf.length;
    const variance = Math.max(0, this.sumSq / this.buf.length - mean * mean);
    const sigma = Math.max(this.sigmaFloor, Math.sqrt(variance));
    // Signed deviation — preserves up/down direction. Magnitude is what
    // crosses the threshold; sign is what tells pothole from speed bump.
    const devSigned = aVert - mean;
    const devAbs = Math.abs(devSigned);

    if (devAbs > this.k * sigma) {
      // Spike. Track the signed peak — i.e. the deviation with the
      // largest magnitude, keeping its sign.
      if (this.candidateStartTs === 0) {
        this.candidateStartTs = ts;
        this.peakSignedSinceCandidate = devSigned;
        return null;
      }
      if (Math.abs(devSigned) > Math.abs(this.peakSignedSinceCandidate)) {
        this.peakSignedSinceCandidate = devSigned;
      }
      return null;
    }

    // No spike in this sample — if we have a pending candidate, fire it.
    if (this.candidateStartTs !== 0) {
      const peakSigned = this.peakSignedSinceCandidate;
      const peakMag = Math.abs(peakSigned);
      const durationMs = Math.max(20, ts - this.candidateStartTs);
      const event = this.makeEvent(gps, peakMag, peakSigned, durationMs, ts);
      this.candidateStartTs = 0;
      this.peakSignedSinceCandidate = 0;
      this.lastFireTs = ts;
      return event;
    }
    return null;
  }

  private pushSample(v: number) {
    this.buf.push(v);
    this.sum += v;
    this.sumSq += v * v;
    if (this.buf.length > this.windowSamples) {
      const dropped = this.buf.shift()!;
      this.sum -= dropped;
      this.sumSq -= dropped * dropped;
    }
  }

  private makeEvent(
    gps: GpsSample,
    peakG: number,
    peakVertSigned: number,
    durationMs: number,
    ts: number,
  ): HitEvent | null {
    const speedFactor = Math.min(1, gps.speedKmh / 40);
    const gpsFactor = gps.accuracyM > 0 ? Math.min(1, 10 / gps.accuracyM) : 1;
    const confidence = Math.min(1, peakG / this.thresholdG) * speedFactor * gpsFactor;
    if (confidence < 0.3) return null;

    return {
      kind: 'hit',
      lat: gps.lat,
      lon: gps.lon,
      speedKmh: gps.speedKmh,
      peakG,
      peakVertSigned,
      durationMs,
      confidence,
      deviceClass: this.deviceClass,
      sampleRateHz: this.sampleRateHz,
      ts,
    };
  }
}
